/**
 * Institutions — the schools and companies EGBA serves.
 *
 * This is the door into the product: an order belongs to an institution, so
 * until one exists nothing else can happen. The screen is built around the two
 * acts that actually matter — recording the regulatory standing, and giving
 * someone access — because those are what unblock a publication.
 *
 * The CNPJ lookup fills the registration from the Receita Federal base. Typing
 * a legal name by hand is how the same school ends up in the base three times
 * with three spellings, and then nobody can tell whether it already published.
 */

import { formatarCnpj, limparCnpj } from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, type Item, type Lista } from '../api/cliente'
import {
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Cartao,
  Carregando,
  Etiqueta,
  Modal,
  Selecao,
  Tabela,
  Vazio,
  formatarData,
} from '../componentes/ui'

interface Instituicao {
  id: string
  cnpj: string
  razaoSocial: string
  nomeFantasia: string | null
  situacaoRegulatoria: string
  ativa: boolean
  criadoEm: string
  pedidos: number
  usuarios: number
}

interface Acesso {
  usuarioId: string
  nome: string
  email: string
  papel: string
  ativo: boolean
  ultimoLoginEm: string | null
}

interface Detalhe extends Instituicao {
  acessos: Acesso[]
  totalPedidos: number
  regularidadeVerificadaEm: string | null
}

/**
 * The regulatory standings, with what each one means for publishing.
 *
 * `desconhecida` blocks — Art. 5º only clears an institution that is
 * demonstrably regular, and "we never checked" is not the same as "fine".
 */
const SITUACOES = [
  {
    valor: 'credenciada',
    texto: 'Credenciada',
    ajuda: 'Pode publicar.',
    tom: 'sucesso' as const,
  },
  {
    valor: 'protocolada_em_tramitacao',
    texto: 'Protocolada, em tramitação',
    ajuda: 'Pode publicar enquanto o pedido de credenciamento tramita.',
    tom: 'marca' as const,
  },
  {
    valor: 'desconhecida',
    texto: 'Ainda não verificada',
    ajuda: '⛔ Bloqueia o envio até a EGBA conferir.',
    tom: 'neutro' as const,
  },
  {
    valor: 'irregular',
    texto: 'Irregular',
    ajuda: '⛔ Não pode publicar concluintes (Res. CEE 44/2020, Art. 5º).',
    tom: 'perigo' as const,
  },
  {
    valor: 'descredenciada',
    texto: 'Descredenciada',
    ajuda: '⛔ Não pode publicar concluintes.',
    tom: 'perigo' as const,
  },
]

const PAPEIS = [
  { valor: 'cliente', texto: 'Titular — envia, aprova o valor e recebe a certidão' },
  { valor: 'operador', texto: 'Operador — prepara a relação, não aprova despesa' },
  { valor: 'parceiro', texto: 'Parceiro — contador ou associação, atua na carteira' },
]

export function Instituicoes() {
  const [abertaId, setAbertaId] = useState<string | null>(null)

  if (abertaId) return <DetalheInstituicao id={abertaId} aoVoltar={() => setAbertaId(null)} />
  return <Listagem aoAbrir={setAbertaId} />
}

// ---------------------------------------------------------------------------

function Listagem({ aoAbrir }: { aoAbrir: (id: string) => void }) {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [criando, setCriando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [cnpj, setCnpj] = useState('')
  const [form, setForm] = useState({
    razaoSocial: '',
    nomeFantasia: '',
    municipio: '',
    uf: '',
    cnaePrincipal: '',
    situacaoRegulatoria: 'desconhecida',
  })
  const [consultado, setConsultado] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['organizacoes', busca],
    queryFn: () =>
      api.get<Lista<Instituicao> & { porSituacao: Record<string, number> }>(
        `/api/v1/organizacoes${busca.trim() ? `?busca=${encodeURIComponent(busca.trim())}` : ''}`,
      ),
  })

  const consultar = useMutation({
    mutationFn: () =>
      api.get<{
        dados: {
          razaoSocial: string
          nomeFantasia: string | null
          municipio: string
          uf: string
          cnaePrincipal: string
        } | null
        jaCadastrada: { id: string; razaoSocial: string } | null
        encontradaNaReceita?: boolean
        baseIndisponivel?: boolean
      }>(`/api/v1/organizacoes/consultar-cnpj/${limparCnpj(cnpj)}`),
    onSuccess: (r) => {
      setConsultado(true)
      setErro(null)
      if (r.jaCadastrada) {
        setErro(`Já cadastrada: ${r.jaCadastrada.razaoSocial}.`)
        return
      }
      if (r.dados) {
        setForm({
          razaoSocial: r.dados.razaoSocial,
          nomeFantasia: r.dados.nomeFantasia ?? '',
          municipio: r.dados.municipio,
          uf: r.dados.uf,
          cnaePrincipal: r.dados.cnaePrincipal,
          situacaoRegulatoria: 'desconhecida',
        })
      } else if (r.baseIndisponivel) {
        setErro('A base da Receita não respondeu. Você pode preencher à mão.')
      } else {
        setErro('CNPJ não encontrado na base da Receita. Preencha à mão.')
      }
    },
    onError: (e: Error) => setErro(e.message),
  })

  const criar = useMutation({
    mutationFn: () => {
      // Campo opcional só aceita AUSÊNCIA, não string vazia: mandar `uf: ''`
      // reprovava todo cadastro manual, que é justamente o caminho de quem não
      // achou o CNPJ na base da Receita.
      const limpo = Object.fromEntries(
        Object.entries(form).filter(([, v]) => String(v ?? '').trim() !== ''),
      )
      return api.post<Item<Instituicao>>('/api/v1/organizacoes', {
        cnpj,
        situacaoRegulatoria: form.situacaoRegulatoria,
        ...limpo,
      })
    },
    onSuccess: (r) => {
      setCriando(false)
      void qc.invalidateQueries({ queryKey: ['organizacoes'] })
      aoAbrir(r.dados.id)
    },
    onError: (e: Error) => setErro(e.message),
  })

  function abrirNovo() {
    setCnpj('')
    setConsultado(false)
    setErro(null)
    setForm({
      razaoSocial: '',
      nomeFantasia: '',
      municipio: '',
      uf: '',
      cnaePrincipal: '',
      situacaoRegulatoria: 'desconhecida',
    })
    setCriando(true)
  }

  if (isLoading) return <Carregando />

  const lista = data?.dados ?? []
  // Do servidor, não da página: contar sobre `dados` dava o número da página
  // visível e do filtro de busca, apresentado como se fosse o panorama do
  // cadastro inteiro.
  const semVerificar = data?.porSituacao?.desconhecida ?? 0

  return (
    <div className="space-y-6">
      {semVerificar > 0 && (
        <Aviso tom="atencao" titulo={`${semVerificar} instituição(ões) sem situação verificada`}>
          Enquanto o credenciamento não é conferido, o envio da relação de concluintes fica
          bloqueado — a Resolução CEE nº 44/2020, Art. 5º, exige instituição regular.
        </Aviso>
      )}

      <Cartao
        titulo="Instituições"
        descricao="Escolas e empresas atendidas pela EGBA. Cada pedido de publicação pertence a uma delas."
        acoes={<Botao onClick={abrirNovo}>Cadastrar instituição</Botao>}
      >
        <div className="mb-5 max-w-md">
          <Campo
            rotulo="Buscar"
            placeholder="Razão social, nome fantasia ou CNPJ"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        {lista.length === 0 ? (
          <Vazio
            titulo={busca ? 'Nada encontrado' : 'Nenhuma instituição cadastrada'}
            descricao={
              busca
                ? 'Ajuste a busca ou cadastre a instituição.'
                : 'Cadastre a primeira escola para que ela possa enviar a relação de concluintes.'
            }
            acao={<Botao onClick={abrirNovo}>Cadastrar instituição</Botao>}
          />
        ) : (
          <Tabela>
            <Cabecalho
              colunas={['Instituição', 'CNPJ', 'Situação', 'Pedidos', 'Acessos', 'Cadastrada', '']}
            />
            <tbody className="divide-y divide-[color:var(--color-borda-suave)]">
              {lista.map((i) => {
                const s = SITUACOES.find((x) => x.valor === i.situacaoRegulatoria)
                return (
                  <tr key={i.id} className="hover:bg-slate-50/70">
                    <td className="max-w-64 px-4 py-3">
                      <p className="truncate font-medium text-slate-900">{i.razaoSocial}</p>
                      {i.nomeFantasia && (
                        <p className="truncate text-xs text-slate-500">{i.nomeFantasia}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 tabular">
                      {formatarCnpj(i.cnpj)}
                    </td>
                    <td className="px-4 py-3">
                      <Etiqueta tom={s?.tom ?? 'neutro'}>{s?.texto ?? i.situacaoRegulatoria}</Etiqueta>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700 tabular">{i.pedidos}</td>
                    <td className="px-4 py-3 text-right text-slate-700 tabular">{i.usuarios}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatarData(i.criadoEm)}</td>
                    <td className="px-4 py-3 text-right">
                      <Botao variante="secundario" tamanho="sm" onClick={() => aoAbrir(i.id)}>
                        Abrir
                      </Botao>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Tabela>
        )}
      </Cartao>

      {/* --------------------------------------------------------- cadastrar */}
      <Modal aberto={criando} aoFechar={() => setCriando(false)} titulo="Cadastrar instituição">
        <div className="space-y-4">
          {erro && <Aviso tom="atencao">{erro}</Aviso>}

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Campo
                rotulo="CNPJ"
                obrigatorio
                placeholder="00.000.000/0000-00"
                value={cnpj}
                onChange={(e) => {
                  setCnpj(e.target.value)
                  setConsultado(false)
                }}
                ajuda="O sistema preenche o resto pela base da Receita Federal."
              />
            </div>
            <Botao
              variante="secundario"
              carregando={consultar.isPending}
              disabled={limparCnpj(cnpj).length !== 14}
              onClick={() => consultar.mutate()}
            >
              Buscar
            </Botao>
          </div>

          {(consultado || form.razaoSocial) && (
            <>
              <Campo
                rotulo="Razão social"
                obrigatorio
                value={form.razaoSocial}
                onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })}
              />
              <Campo
                rotulo="Nome fantasia"
                value={form.nomeFantasia}
                onChange={(e) => setForm({ ...form, nomeFantasia: e.target.value })}
              />
              <div className="grid grid-cols-[1fr_80px] gap-4">
                <Campo
                  rotulo="Município"
                  value={form.municipio}
                  onChange={(e) => setForm({ ...form, municipio: e.target.value })}
                />
                <Campo
                  rotulo="UF"
                  maxLength={2}
                  value={form.uf}
                  onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })}
                />
              </div>
              <Selecao
                rotulo="Situação regulatória"
                value={form.situacaoRegulatoria}
                onChange={(e) => setForm({ ...form, situacaoRegulatoria: e.target.value })}
                opcoes={SITUACOES.map((s) => ({ valor: s.valor, texto: s.texto }))}
                ajuda={SITUACOES.find((s) => s.valor === form.situacaoRegulatoria)?.ajuda}
              />
            </>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <Botao variante="secundario" onClick={() => setCriando(false)}>
            Cancelar
          </Botao>
          <Botao
            carregando={criar.isPending}
            disabled={limparCnpj(cnpj).length !== 14 || form.razaoSocial.trim().length < 3}
            onClick={() => criar.mutate()}
          >
            Cadastrar
          </Botao>
        </div>
      </Modal>
    </div>
  )
}

// ---------------------------------------------------------------------------

function DetalheInstituicao({ id, aoVoltar }: { id: string; aoVoltar: () => void }) {
  const qc = useQueryClient()
  const [convidando, setConvidando] = useState(false)
  const [convite, setConvite] = useState({ nome: '', email: '', papel: 'cliente' })
  const [senhaGerada, setSenhaGerada] = useState<{ email: string; senha: string } | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['organizacao', id],
    queryFn: () => api.get<Item<Detalhe>>(`/api/v1/organizacoes/${id}`),
  })

  const mudarSituacao = useMutation({
    mutationFn: (situacaoRegulatoria: string) =>
      api.put<Item<Detalhe>>(`/api/v1/organizacoes/${id}`, { situacaoRegulatoria }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organizacao', id] })
      // A listagem tem outra chave e um staleTime de 30s: sem isto, voltar da
      // ficha mostrava a escola ainda como "Ainda não verificada" e o aviso do
      // topo continuava contando-a.
      void qc.invalidateQueries({ queryKey: ['organizacoes'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  const convidar = useMutation({
    mutationFn: () => api.post<Item<{ senhaProvisoria: string; jaExistia: boolean }>>(
      `/api/v1/organizacoes/${id}/acessos`,
      convite,
    ),
    onSuccess: (r) => {
      setConvidando(false)
      setSenhaGerada(
        r.dados.jaExistia
          ? { email: convite.email, senha: '' }
          : { email: convite.email, senha: r.dados.senhaProvisoria },
      )
      setConvite({ nome: '', email: '', papel: 'cliente' })
      void qc.invalidateQueries({ queryKey: ['organizacao', id] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  const revogar = useMutation({
    mutationFn: (usuarioId: string) =>
      api.delete(`/api/v1/organizacoes/${id}/acessos/${usuarioId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['organizacao', id] }),
  })

  if (isLoading || !data) return <Carregando />
  const org = data.dados
  const s = SITUACOES.find((x) => x.valor === org.situacaoRegulatoria)

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={aoVoltar}
          className="mb-1 text-sm text-slate-500 hover:text-slate-900"
        >
          ← Instituições
        </button>
        <h2 className="text-xl font-semibold text-slate-900">{org.razaoSocial}</h2>
        <p className="text-sm text-slate-500 tabular">{formatarCnpj(org.cnpj)}</p>
      </div>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {senhaGerada && (
        <Aviso tom="sucesso" titulo="Acesso criado">
          {senhaGerada.senha ? (
            <>
              <p>
                Senha provisória de <strong>{senhaGerada.email}</strong>:
              </p>
              <p className="my-2 rounded bg-white/70 px-3 py-2 font-mono text-base">
                {senhaGerada.senha}
              </p>
              <p className="text-[13px]">
                Entregue por um canal seguro. Ela vale para o primeiro acesso, e a pessoa vai
                precisar cadastrar a verificação em duas etapas.
              </p>
            </>
          ) : (
            <p>
              <strong>{senhaGerada.email}</strong> já tinha conta e ganhou acesso a esta
              instituição. A senha atual continua valendo.
            </p>
          )}
        </Aviso>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Cartao titulo="Situação regulatória">
          <div className="mb-4 flex items-center gap-3">
            <Etiqueta tom={s?.tom ?? 'neutro'}>{s?.texto ?? org.situacaoRegulatoria}</Etiqueta>
            {org.regularidadeVerificadaEm && (
              <span className="text-xs text-slate-400">
                verificada em {formatarData(org.regularidadeVerificadaEm)}
              </span>
            )}
          </div>

          <Selecao
            rotulo="Alterar situação"
            value={org.situacaoRegulatoria}
            disabled={mudarSituacao.isPending}
            onChange={(e) => mudarSituacao.mutate(e.target.value)}
            opcoes={SITUACOES.map((x) => ({ valor: x.valor, texto: x.texto }))}
            ajuda={s?.ajuda}
          />

          <p className="mt-3 text-xs text-slate-500">
            Quem alterar e quando fica registrado — é esta escolha que libera ou barra a
            publicação dos concluintes.
          </p>
        </Cartao>

        <Cartao
          titulo="Acessos"
          descricao="Quem pode operar esta instituição no painel."
          acoes={
            <Botao variante="secundario" tamanho="sm" onClick={() => setConvidando(true)}>
              Convidar
            </Botao>
          }
        >
          {org.acessos.length === 0 ? (
            <p className="py-4 text-sm text-slate-500">
              Ninguém tem acesso ainda. Sem um acesso, a escola não consegue enviar a relação.
            </p>
          ) : (
            <ul className="divide-y divide-[color:var(--color-borda-suave)]">
              {org.acessos.map((a) => (
                <li key={a.usuarioId} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{a.nome}</p>
                    <p className="truncate text-xs text-slate-500">{a.email}</p>
                    <p className="text-xs text-slate-400">
                      {PAPEIS.find((p) => p.valor === a.papel)?.texto.split(' — ')[0] ?? a.papel}
                      {a.ultimoLoginEm
                        ? ` · último acesso ${formatarData(a.ultimoLoginEm)}`
                        : ' · nunca entrou'}
                    </p>
                  </div>
                  <Botao
                    variante="secundario"
                    tamanho="sm"
                    carregando={revogar.isPending}
                    onClick={() => revogar.mutate(a.usuarioId)}
                  >
                    Revogar
                  </Botao>
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      </div>

      <Cartao titulo="Publicações">
        <p className="text-3xl font-semibold text-slate-900 tabular">{org.totalPedidos}</p>
        <p className="mt-1 text-sm text-slate-500">pedidos registrados por esta instituição</p>
      </Cartao>

      <Modal aberto={convidando} aoFechar={() => setConvidando(false)} titulo="Convidar acesso">
        <div className="space-y-4">
          <Campo
            rotulo="Nome"
            obrigatorio
            value={convite.nome}
            onChange={(e) => setConvite({ ...convite, nome: e.target.value })}
          />
          <Campo
            rotulo="E-mail"
            type="email"
            obrigatorio
            value={convite.email}
            onChange={(e) => setConvite({ ...convite, email: e.target.value })}
          />
          <Selecao
            rotulo="Papel"
            value={convite.papel}
            onChange={(e) => setConvite({ ...convite, papel: e.target.value })}
            opcoes={PAPEIS.map((p) => ({ valor: p.valor, texto: p.texto }))}
          />
          <Aviso tom="info">
            A senha provisória aparece na tela depois de criar — não é enviada por e-mail. Entregue
            por um canal seguro.
          </Aviso>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <Botao variante="secundario" onClick={() => setConvidando(false)}>
            Cancelar
          </Botao>
          <Botao
            carregando={convidar.isPending}
            disabled={convite.nome.trim().length < 3 || !convite.email.includes('@')}
            onClick={() => convidar.mutate()}
          >
            Criar acesso
          </Botao>
        </div>
      </Modal>
    </div>
  )
}
